// src/components/admin/scheduling/eventUtils.js
// Utilities for recurring event expansion and lane layout

const DOW_MAP = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function parseDateKey(dateKey) {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function getWeekMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function diffDays(a, b) {
  return Math.round((a - b) / (24 * 60 * 60 * 1000));
}

function countWeeklyOccurrencesUpTo(startDate, targetDate, interval, weekDays) {
  let count = 0;
  const startMon = getWeekMonday(startDate);
  let current = new Date(startDate);

  while (current <= targetDate) {
    const dow = DOW_MAP[current.getDay()];
    if (weekDays.includes(dow)) {
      const currentMon = getWeekMonday(current);
      const weeksDiff = diffDays(currentMon, startMon) / 7;
      if (weeksDiff >= 0 && weeksDiff % interval === 0) {
        count += 1;
      }
    }
    current = new Date(current.getTime() + 24 * 60 * 60 * 1000);
  }
  return count;
}

export function doesEventOccurOnDate(event, targetDateKey) {
  const startDateKey = event.date;
  if (!startDateKey || targetDateKey < startDateKey) return false;

  const recurrence = event.recurrence;

  if (!recurrence?.enabled) {
    return startDateKey === targetDateKey;
  }

  const { frequency, interval = 1, weekDays = [], monthlyDay, ends } = recurrence;

  if (ends?.type === "on" && ends.endDate && targetDateKey > ends.endDate) {
    return false;
  }

  const startDate = parseDateKey(startDateKey);
  const targetDate = parseDateKey(targetDateKey);

  if (frequency === "daily") {
    const daysDiff = diffDays(targetDate, startDate);
    if (daysDiff % interval !== 0) return false;
    if (ends?.type === "after" && ends.occurrences) {
      const occurrenceNum = daysDiff / interval + 1;
      if (occurrenceNum > ends.occurrences) return false;
    }
    return true;
  }

  if (frequency === "weekly") {
    const targetDow = DOW_MAP[targetDate.getDay()];
    const effectiveDays = weekDays.length > 0 ? weekDays : [DOW_MAP[startDate.getDay()]];
    if (!effectiveDays.includes(targetDow)) return false;

    const startMon = getWeekMonday(startDate);
    const targetMon = getWeekMonday(targetDate);
    const weeksDiff = diffDays(targetMon, startMon) / 7;
    if (weeksDiff < 0 || weeksDiff % interval !== 0) return false;

    if (ends?.type === "after" && ends.occurrences) {
      const count = countWeeklyOccurrencesUpTo(startDate, targetDate, interval, effectiveDays);
      if (count > ends.occurrences) return false;
    }
    return true;
  }

  if (frequency === "monthly") {
    const targetDay = targetDate.getDate();
    const expectedDay = monthlyDay || startDate.getDate();
    if (targetDay !== expectedDay) return false;

    const monthsDiff =
      (targetDate.getFullYear() - startDate.getFullYear()) * 12 +
      (targetDate.getMonth() - startDate.getMonth());
    if (monthsDiff < 0 || monthsDiff % interval !== 0) return false;

    if (ends?.type === "after" && ends.occurrences) {
      const occurrenceNum = monthsDiff / interval + 1;
      if (occurrenceNum > ends.occurrences) return false;
    }
    return true;
  }

  return false;
}

export function computeEventsForDate(allEvents, dateKey) {
  if (!Array.isArray(allEvents) || !dateKey) return [];
  return allEvents.filter((ev) => doesEventOccurOnDate(ev, dateKey));
}

// Compute lane assignments for events (no max-concurrent limit)
export function computeEventLanes(events = []) {
  const sorted = [...events].sort((a, b) => {
    if (a.startTime !== b.startTime) return a.startTime - b.startTime;
    return a.endTime - b.endTime;
  });

  const laneEnds = [];

  return sorted.map((event) => {
    const idx = laneEnds.findIndex((end) => end <= event.startTime);
    const lane = idx === -1 ? laneEnds.length : idx;
    if (idx === -1) {
      laneEnds.push(event.endTime);
    } else {
      laneEnds[idx] = Math.max(laneEnds[idx], event.endTime);
    }
    return { ...event, lane };
  });
}

// Build a human-readable recurrence label (for display in UI)
export function buildRecurrenceLabel(recurrence) {
  if (!recurrence?.enabled) return "Does not repeat";

  const { frequency, interval = 1, weekDays = [], monthlyDay, ends } = recurrence;

  const freqLabel = frequency === "daily" ? "day" : frequency === "weekly" ? "week" : "month";
  const intervalStr = interval === 1 ? `Every ${freqLabel}` : `Every ${interval} ${freqLabel}s`;

  let dayStr = "";
  if (frequency === "weekly" && weekDays.length > 0) {
    const abbrevs = { sunday: "Sun", monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat" };
    dayStr = ` on ${weekDays.map((d) => abbrevs[d] || d).join(", ")}`;
  }
  if (frequency === "monthly" && monthlyDay) {
    dayStr = ` on day ${monthlyDay}`;
  }

  let endsStr = "";
  if (ends?.type === "on" && ends.endDate) endsStr = `, until ${ends.endDate}`;
  if (ends?.type === "after" && ends.occurrences) endsStr = `, ${ends.occurrences} times`;

  return `${intervalStr}${dayStr}${endsStr}`;
}
